import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserPlus, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, useAuth, formatApiError } from "@/lib/auth";
import { EndClientDialog } from "@/components/intermediary/EndClientDialog";

function Choice({ checked, onChange, label, hint, testId, disabled }) {
  return (
    <label className={`flex items-start gap-2 border px-3 py-2 text-sm ${checked ? "border-[#0E7C90] bg-[#0E7C90]/5" : "border-black/15 bg-white"} ${disabled ? "opacity-60" : "cursor-pointer"}`}>
      <input type="radio" checked={checked} onChange={onChange} disabled={disabled} className="mt-1" data-testid={testId} />
      <span><span className="font-medium">{label}</span>{hint && <span className="block text-[12px] text-neutral-500">{hint}</span>}</span>
    </label>
  );
}

function Summary({ order }) {
  return (
    <div className="mt-3 text-sm space-y-1" data-testid="billing-setup-summary">
      <div><span className="text-neutral-500">Client final : </span><strong data-testid="billing-summary-end-client">{order.end_client?.name || "Aucun (projet propre)"}</strong></div>
      <div><span className="text-neutral-500">Facturé à : </span><strong data-testid="billing-summary-party">{order.billing_party === "end_client" ? "le client final" : "le titulaire du compte"}</strong></div>
      <div><span className="text-neutral-500">Règlement : </span><strong data-testid="billing-summary-payer">{order.payer === "end_client" ? "par le client final" : "par le titulaire du compte"}</strong></div>
    </div>
  );
}

export function OrderBillingSetup({ order, isAdmin, onReload }) {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [crid, setCrid] = useState(order.end_client?.id || "");
  const [party, setParty] = useState(order.billing_party || "account");
  const [payer, setPayer] = useState(order.payer || "account");
  const intermediary = ["clients", "both"].includes(user?.account_usage);
  const [editing, setEditing] = useState(!order.billing_confirmed && intermediary);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(false);

  const loadClients = async () => {
    const { data } = await api.get("/me/end-clients");
    setClients(data.clients || []);
  };
  useEffect(() => { if (!isAdmin) { loadClients(); } }, [isAdmin]);

  if (isAdmin) {
    if (!order.end_client) return null;
    return <div className="cs-card p-6" data-testid="billing-setup-admin"><div className="cs-eyebrow text-[#0E7C90]">Client final & facturation</div><Summary order={order} /></div>;
  }
  if (!intermediary && !order.end_client && !editing && !order.billing_locked) {
    return (
      <div className="cs-card px-6 py-4 text-[13px] text-neutral-600 flex flex-wrap items-center justify-between gap-2" data-testid="billing-setup-compact">
        <span>Cette commande est pour l'un de vos clients ?</span>
        <button onClick={() => setEditing(true)} className="font-mono underline" data-testid="billing-setup-open">Indiquer un client final</button>
      </div>
    );
  }

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${order.id}/billing-setup`, {
        end_client_record_id: crid || null,
        billing_party: crid ? party : "account",
        payer: crid && party === "end_client" ? payer : "account",
      });
      toast.success("Informations de facturation de la commande enregistrées");
      setEditing(false);
      onReload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className={`cs-card p-6 ${!order.billing_confirmed && intermediary ? "border-[#0E7C90]" : ""}`} data-testid="billing-setup">
      <div className="flex items-center justify-between gap-3">
        <div className="cs-eyebrow text-[#0E7C90] inline-flex items-center gap-2"><Users size={13} /> Client final & facturation</div>
        {!editing && <button onClick={() => setEditing(true)} className="text-[12px] font-mono underline" data-testid="billing-setup-edit">Modifier</button>}
      </div>
      {!editing && <Summary order={order} />}
      {!editing && order.billing_locked && <p className="mt-3 text-[12px] font-mono text-neutral-500 inline-flex items-center gap-1"><Lock size={11} /> Facture émise : seul le mode de règlement des étapes restantes reste modifiable.</p>}
      {editing && (
        <div className="mt-4 space-y-4">
          {!order.billing_confirmed && intermediary && <p className="text-sm text-neutral-700">Avant de régler, indiquez pour qui est cette commande et qui doit être facturé.</p>}
          <div>
            <div className="cs-eyebrow text-neutral-500">Pour qui est ce projet ?</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <select value={crid} onChange={(e) => setCrid(e.target.value)} disabled={order.billing_locked} className="cs-input flex-1 min-w-[220px]" data-testid="billing-setup-client-select">
                <option value="">Mon propre projet</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {!order.billing_locked && <button type="button" onClick={() => setDialog(true)} className="cs-btn-ghost !text-black !border-black !py-2 text-[13px]" data-testid="billing-setup-add-client"><UserPlus size={13} /> <span>Nouveau client</span></button>}
            </div>
          </div>
          {crid && (
            <div>
              <div className="cs-eyebrow text-neutral-500">Qui reçoit la facture ?</div>
              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                <Choice checked={party === "account"} onChange={() => setParty("account")} disabled={order.billing_locked} label="Moi (titulaire du compte)" hint="Facture à vos coordonnées de facturation." testId="billing-party-account" />
                <Choice checked={party === "end_client"} onChange={() => { setParty("end_client"); setPayer("end_client"); }} disabled={order.billing_locked} label="Mon client final" hint="Facture émise à son nom, mention « Commande passée par » vous." testId="billing-party-end-client" />
              </div>
            </div>
          )}
          {crid && party === "end_client" && (
            <div>
              <div className="cs-eyebrow text-neutral-500">Qui règle ?</div>
              <div className="mt-2 grid sm:grid-cols-2 gap-2">
                <Choice checked={payer === "end_client"} onChange={() => setPayer("end_client")} label="Mon client final" hint="Charbonier Studio lui adresse une demande de règlement sécurisée." testId="payer-end-client" />
                <Choice checked={payer === "account"} onChange={() => setPayer("account")} label="Moi, pour son compte" hint="Vous réglez en ligne depuis cette commande." testId="payer-account" />
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} className="cs-btn-primary" data-testid="billing-setup-save">{busy && <Loader2 size={13} className="animate-spin" />} Confirmer</button>
            {order.billing_confirmed && <button onClick={() => setEditing(false)} className="cs-btn-ghost !text-black !border-black" data-testid="billing-setup-cancel"><span>Annuler</span></button>}
          </div>
          <p className="text-[12px] text-neutral-500">Gérez vos clients dans <Link to="/espace/clients" className="cs-link-underline">Mes clients</Link>.</p>
        </div>
      )}
      <EndClientDialog open={dialog} onOpenChange={setDialog} onSaved={async (c) => { await loadClients(); setCrid(c.id); }} />
    </div>
  );
}
